import api from './api';

export interface ContextOption {
  value: string;
  label: string;
  /** Section of the context picker: "Entities", "Questionnaires" or
   *  "Send Questionnaire". Sent by the server so both template screens group
   *  the same way without re-deriving it from the value prefix. */
  group?: string;
}

export interface AvailableVariable {
  name: string;
  description: string;
  example: string;
}

export interface AvailableVariablesResponse {
  context: string;
  variables: AvailableVariable[];
}

export interface SmsTemplate {
  id: number;
  tenant_id: number;
  context: string;
  name: string;
  internal_id?: string | null;
  content: string;
  language: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SmsTemplateCreate {
  context: string;
  name: string;
  internal_id?: string;
  content: string;
  language?: string;
  is_active?: boolean;
}

export interface SmsTemplateUpdate {
  context?: string;
  name?: string;
  internal_id?: string;
  content?: string;
  language?: string;
  is_active?: boolean;
}

export interface SmsTemplatePreviewRequest {
  variables: Record<string, any>;
}

export interface SmsTemplatePreviewResponse {
  original_content: string;
  formatted_content: string;
  missing_variables: string[];
}

class SmsTemplateService {
  async getTemplates(context?: string, isActive?: boolean): Promise<SmsTemplate[]> {
    const params = new URLSearchParams();
    if (context) params.append('context', context);
    if (isActive !== undefined) params.append('is_active', String(isActive));

    const response = await api.get(`/sms-templates/?${params.toString()}`);
    return response.data.items;
  }

  async getTemplate(id: number): Promise<SmsTemplate> {
    const response = await api.get(`/sms-templates/${id}`);
    return response.data;
  }

  async createTemplate(template: SmsTemplateCreate): Promise<SmsTemplate> {
    const response = await api.post('/sms-templates/', template);
    return response.data;
  }

  async updateTemplate(id: number, template: SmsTemplateUpdate): Promise<SmsTemplate> {
    const response = await api.put(`/sms-templates/${id}`, template);
    return response.data;
  }

  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`/sms-templates/${id}`);
  }

  async getAvailableVariables(context: string): Promise<AvailableVariable[]> {
    const response = await api.get(`/sms-templates/contexts/${context}/variables`);
    return response.data.variables;
  }

  async getContexts(): Promise<ContextOption[]> {
    const response = await api.get('/sms-templates/contexts');
    return response.data.contexts;
  }

  async previewTemplate(id: number, variables: Record<string, any>): Promise<SmsTemplatePreviewResponse> {
    const response = await api.post(`/sms-templates/${id}/preview`, {
      variables,
    });
    return response.data;
  }
}

const smsTemplateService = new SmsTemplateService();
export default smsTemplateService;
